import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ScheduleV3State,
  initialScheduleV3State,
  ScheduleV3Group,
  ScheduleV3FilterState,
  ScheduleV3UIState,
  SchedulesV3GroupByType,
  ScheduleV3Conflict,
  ScheduleV3DateData,
  ScheduleV3Unavailability,
  ScheduleV3Timeoff,
  ScheduleV3Holiday,
} from '../../../types';
import { getSchedulesV3, addSchedule, getScheduleSidebarList } from './scheduleV3Api';

/**
 * Transform ungrouped data (like unavailability, timeoff) into proper employee/department groups
 * This handles cases where the API returns data with generic group IDs like "ungrouped_emp"
 * and redistributes them to their actual employee/department groups based on the employee field
 */
const transformUngroupedData = (
  newGroups: ScheduleV3Group[],
  groupBy: SchedulesV3GroupByType,
  existingGroups: ScheduleV3Group[]
): ScheduleV3Group[] => {
  const transformedGroups: ScheduleV3Group[] = [];
  const ungroupedGroups: ScheduleV3Group[] = [];

  // Special handling for tag-grouped open shifts:
  // Backend can return a duplicate/extra group with empty group_id/group_name containing open_shifts
  // that are already tagged. We redistribute those open_shifts into their tag groups (deduped).
  if (groupBy === 'tags') {
    const emptyTagIndex = newGroups.findIndex(g => (g.group_id ?? '') === '' && (g.group_name ?? '') === '');
    if (emptyTagIndex > -1) {
      const emptyTagGroup = newGroups[emptyTagIndex];
      const remainingGroups = newGroups.filter((_, idx) => idx !== emptyTagIndex);

      // Index existing groups by group_id for quick lookup
      const byId = new Map<string, ScheduleV3Group>();
      remainingGroups.forEach(g => {
        if (!byId.has(g.group_id)) byId.set(g.group_id, g);
      });

      const dedupeKey = (os: any) => String(os?.open_shift_id ?? '');

      Object.entries(emptyTagGroup.dates || {}).forEach(([dateKey, dateData]: any) => {
        const list = Array.isArray(dateData?.open_shifts) ? dateData.open_shifts : [];
        list.forEach((openShift: any) => {
          // Find first non-empty tag value_code/value_text across shift_tags
          let tagCode = '';
          let tagName = '';
          const tags = Array.isArray(openShift?.shift_tags) ? openShift.shift_tags : [];
          for (const t of tags) {
            const values = Array.isArray(t?.field_values) ? t.field_values : [];
            for (const v of values) {
              const code = String(v?.value_code ?? '').trim();
              const text = String(v?.value_text ?? '').trim();
              if (code) {
                tagCode = code;
                tagName = text || code;
                break;
              }
            }
            if (tagCode) break;
          }

          const targetId = tagCode || 'ungrouped_tags';
          const targetName = tagCode ? tagName : 'Tags';

          let targetGroup = byId.get(targetId);
          if (!targetGroup) {
            targetGroup = {
              group_id: targetId,
              group_name: targetName,
              dates: {},
            };
            byId.set(targetId, targetGroup);
            remainingGroups.push(targetGroup);
          }

          if (!targetGroup.dates[dateKey]) {
            targetGroup.dates[dateKey] = {
              schedules: [],
              timeoff: [],
              unavailability: [],
              holiday: [],
              open_shifts: [],
            };
          }

          const existing = Array.isArray(targetGroup.dates[dateKey].open_shifts)
            ? targetGroup.dates[dateKey].open_shifts
            : [];

          const key = dedupeKey(openShift);
          if (!key) return;
          if (existing.some((o: any) => dedupeKey(o) === key)) return;

          targetGroup.dates[dateKey].open_shifts = [...existing, openShift];
        });
      });

      newGroups = remainingGroups;
    }
  }
  
  // Separate ungrouped and properly grouped data
  newGroups.forEach(group => {
    // Only treat true ungrouped buckets as ungrouped. `open_shifts` is a legitimate group_id that
    // some API responses return (especially when grouping by employee) and should render as-is.
    if (group.group_id === 'ungrouped_emp' || group.group_id === 'ungrouped_dept') {
      ungroupedGroups.push(group);
    } else {
      transformedGroups.push(group);
    }
  });
  
  // If no ungrouped data, return as is
  if (ungroupedGroups.length === 0) {
    return newGroups;
  }
  
  console.log('[ScheduleV3] Transforming ungrouped data:', {
    ungroupedGroupsCount: ungroupedGroups.length,
    properGroupsCount: transformedGroups.length,
    groupBy,
  });
  
  // Helper function to get group ID based on grouping type
  const getGroupId = (item: any): string | null => {
    if (groupBy === 'employee' && item.employee?.id) {
      return item.employee.id;
    }
    // For department grouping, try to get department from schedule data
    // This would need to be enhanced based on actual department data structure
    if (groupBy === 'department') {
      // If the item has department info, use it
      if (item.department?.department_id) {
        return item.department.department_id;
      }
      if (item.department?.department_code) {
        return item.department.department_code;
      }
      // For department grouping, if we don't have department info, return null
      // This will keep the data in ungrouped_dept instead of creating employee-based groups
      return null;
    }
    return null;
  };
  
  const getGroupName = (item: any, groupId: string): string => {
    if (groupBy === 'employee' && item.employee?.name) {
      return item.employee.name;
    }
    if (groupBy === 'department' && item.department?.department_name) {
      return item.department.department_name;
    }
    // Fallback: check existing groups
    const existing = existingGroups.find(g => g.group_id === groupId);
    if (existing) {
      return existing.group_name;
    }
    return groupBy === 'employee' ? 'Unknown Employee' : 'Unknown Department';
  };
  
  // Track items that couldn't be redistributed (will stay in ungrouped group)
  const unredistributableData: { [groupId: string]: ScheduleV3Group } = {};
  
  // Process each ungrouped group
  ungroupedGroups.forEach(ungroupedGroup => {
    // Initialize tracking for this ungrouped group
    const groupIdKey = ungroupedGroup.group_id;
    if (!unredistributableData[groupIdKey]) {
      unredistributableData[groupIdKey] = {
        group_id: groupIdKey,
        group_name: ungroupedGroup.group_name,
        dates: {}
      };
    }
    
    // Iterate through each date in the ungrouped data
    Object.entries(ungroupedGroup.dates).forEach(([dateKey, dateData]) => {
      // Preserve open shifts on ungrouped groups as well (API may return open_shifts under ungrouped_dept/ungrouped_emp).
      // If we drop them here, monthly calendar will not render open shifts even when the response includes them.
      if (dateData.open_shifts && dateData.open_shifts.length > 0) {
        // Keep them on the unredistributable group so they still render in UI.
        if (!unredistributableData[groupIdKey].dates[dateKey]) {
          unredistributableData[groupIdKey].dates[dateKey] = {
            schedules: [],
            timeoff: [],
            unavailability: [],
            holiday: [],
            open_shifts: [],
          };
        }
        unredistributableData[groupIdKey].dates[dateKey].open_shifts = [
          ...(unredistributableData[groupIdKey].dates[dateKey].open_shifts || []),
          ...dateData.open_shifts,
        ];
      }

      // Process unavailability items
      if (dateData.unavailability && dateData.unavailability.length > 0) {
        dateData.unavailability.forEach((unavail: ScheduleV3Unavailability) => {
          const groupId = getGroupId(unavail);
          if (groupId) {
            // Find or create the target group
            let targetGroup = transformedGroups.find(g => g.group_id === groupId);
            
            if (!targetGroup) {
              // Check if it exists in existing groups
              const existingGroup = existingGroups.find(g => g.group_id === groupId);
              if (existingGroup) {
                // Clone the existing group structure
                targetGroup = {
                  group_id: groupId,
                  group_name: getGroupName(unavail, groupId) || existingGroup.group_name,
                  dates: { ...existingGroup.dates },
                };
              } else {
                // Create a new group
                targetGroup = {
                  group_id: groupId,
                  group_name: getGroupName(unavail, groupId),
                  dates: {},
                };
              }
              transformedGroups.push(targetGroup);
            }
            
            // Ensure the date exists in the target group
            if (!targetGroup.dates[dateKey]) {
              targetGroup.dates[dateKey] = {
                schedules: [],
                timeoff: [],
                unavailability: [],
                holiday: [],
                open_shifts: [],
              };
            }
            
            // Add the unavailability to the target group's date
            targetGroup.dates[dateKey].unavailability.push(unavail);
          } else {
            // Can't redistribute - keep in unredistributable tracking
            if (!unredistributableData[groupIdKey].dates[dateKey]) {
              unredistributableData[groupIdKey].dates[dateKey] = {
                schedules: [],
                timeoff: [],
                unavailability: [],
                holiday: [],
                open_shifts: [],
              };
            }
            unredistributableData[groupIdKey].dates[dateKey].unavailability.push(unavail);
          }
        });
      }
      
      // Process timeoff items
      if (dateData.timeoff && dateData.timeoff.length > 0) {
        dateData.timeoff.forEach((timeoff: ScheduleV3Timeoff) => {
          const groupId = getGroupId(timeoff);
          if (groupId) {
            // Find or create the target group
            let targetGroup = transformedGroups.find(g => g.group_id === groupId);
            
            if (!targetGroup) {
              // Check if it exists in existing groups
              const existingGroup = existingGroups.find(g => g.group_id === groupId);
              if (existingGroup) {
                // Clone the existing group structure
                targetGroup = {
                  group_id: groupId,
                  group_name: getGroupName(timeoff, groupId) || existingGroup.group_name,
                  dates: { ...existingGroup.dates },
                };
              } else {
                // Create a new group
                targetGroup = {
                  group_id: groupId,
                  group_name: getGroupName(timeoff, groupId),
                  dates: {},
                };
              }
              transformedGroups.push(targetGroup);
            }
            
            // Ensure the date exists in the target group
            if (!targetGroup.dates[dateKey]) {
              targetGroup.dates[dateKey] = {
                schedules: [],
                timeoff: [],
                unavailability: [],
                holiday: [],
                open_shifts: [],
              };
            }
            
            // Add the timeoff to the target group's date
            targetGroup.dates[dateKey].timeoff.push(timeoff);
          } else {
            // Can't redistribute - keep in unredistributable tracking
            if (!unredistributableData[groupIdKey].dates[dateKey]) {
              unredistributableData[groupIdKey].dates[dateKey] = {
                schedules: [],
                timeoff: [],
                unavailability: [],
                holiday: [],
                open_shifts: [],
              };
            }
            unredistributableData[groupIdKey].dates[dateKey].timeoff.push(timeoff);
          }
        });
      }
      
      // Process holiday items
      // Holidays have an employees array, so we need to distribute them to each employee
      if (dateData.holiday && dateData.holiday.length > 0) {
        dateData.holiday.forEach((holiday: ScheduleV3Holiday) => {
          if (holiday.employees && holiday.employees.length > 0) {
            // Distribute this holiday to each employee
            holiday.employees.forEach((holidayEmp) => {
              const groupId = getGroupId(holidayEmp);
              if (groupId) {
                // Find or create the target group
                let targetGroup = transformedGroups.find(g => g.group_id === groupId);
                
                if (!targetGroup) {
                  // Check if it exists in existing groups
                  const existingGroup = existingGroups.find(g => g.group_id === groupId);
                  if (existingGroup) {
                    // Clone the existing group structure
                    targetGroup = {
                      group_id: groupId,
                      group_name: getGroupName(holidayEmp, groupId) || existingGroup.group_name,
                      dates: { ...existingGroup.dates },
                    };
                  } else {
                    // Create a new group
                    targetGroup = {
                      group_id: groupId,
                      group_name: getGroupName(holidayEmp, groupId),
                      dates: {},
                    };
                  }
                  transformedGroups.push(targetGroup);
                }
                
                // Ensure the date exists in the target group
                if (!targetGroup.dates[dateKey]) {
                  targetGroup.dates[dateKey] = {
                    schedules: [],
                    timeoff: [],
                    unavailability: [],
                    holiday: [],
                    open_shifts: [],
                  };
                }
                
                // Add the holiday to the target group's date (avoid duplicates)
                const existingHoliday = targetGroup.dates[dateKey].holiday.find(
                  h => h.title === holiday.title && h.calendar_code === holiday.calendar_code
                );
                if (!existingHoliday) {
                  targetGroup.dates[dateKey].holiday.push(holiday);
                }
              }
            });
          } else {
            // If no employees specified, add to all existing groups (company-wide holiday)
            // This is handled by the normal merge logic, so we don't need to do anything here
          }
        });
      }
    });
  });
  
  // Add back any unredistributable data (items that couldn't be assigned to proper groups)
  // Split into separate rows by data type for better clarity
  Object.values(unredistributableData).forEach(ungroupedData => {
    // Split ungrouped data into separate groups by type
    const timeoffGroup: ScheduleV3Group = {
      group_id: `${ungroupedData.group_id}_timeoff`,
      group_name: 'Timeoff',
      dates: {}
    };
    
    const unavailabilityGroup: ScheduleV3Group = {
      group_id: `${ungroupedData.group_id}_unavailability`,
      group_name: 'Unavailability',
      dates: {}
    };
    
    const holidayGroup: ScheduleV3Group = {
      group_id: `${ungroupedData.group_id}_holiday`,
      group_name: 'Holiday',
      dates: {}
    };

    const openShiftsGroup: ScheduleV3Group = {
      group_id: `${ungroupedData.group_id}_open_shifts`,
      group_name: 'Open Shifts',
      dates: {}
    };
    
    // Distribute data by type
    Object.entries(ungroupedData.dates).forEach(([dateKey, dateData]) => {
      // Add open shifts data
      if (dateData.open_shifts && dateData.open_shifts.length > 0) {
        if (!openShiftsGroup.dates[dateKey]) {
          openShiftsGroup.dates[dateKey] = {
            schedules: [],
            timeoff: [],
            unavailability: [],
            holiday: [],
            open_shifts: [],
          };
        }
        openShiftsGroup.dates[dateKey].open_shifts = [...dateData.open_shifts];
      }

      // Add timeoff data
      if (dateData.timeoff && dateData.timeoff.length > 0) {
        if (!timeoffGroup.dates[dateKey]) {
          timeoffGroup.dates[dateKey] = {
            schedules: [],
            timeoff: [],
            unavailability: [],
            holiday: [],
            open_shifts: [],
          };
        }
        timeoffGroup.dates[dateKey].timeoff = [...dateData.timeoff];
      }
      
      // Add unavailability data
      if (dateData.unavailability && dateData.unavailability.length > 0) {
        if (!unavailabilityGroup.dates[dateKey]) {
          unavailabilityGroup.dates[dateKey] = {
            schedules: [],
            timeoff: [],
            unavailability: [],
            holiday: [],
            open_shifts: [],
          };
        }
        unavailabilityGroup.dates[dateKey].unavailability = [...dateData.unavailability];
      }
      
      // Add holiday data
      if (dateData.holiday && dateData.holiday.length > 0) {
        if (!holidayGroup.dates[dateKey]) {
          holidayGroup.dates[dateKey] = {
            schedules: [],
            timeoff: [],
            unavailability: [],
            holiday: [],
            open_shifts: [],
          };
        }
        holidayGroup.dates[dateKey].holiday = [...dateData.holiday];
      }
    });
    
    // Add each type as a separate group if it has data
    if (Object.keys(timeoffGroup.dates).length > 0) {
      const timeoffCount = Object.values(timeoffGroup.dates).reduce((sum, d) => sum + (d.timeoff?.length || 0), 0);
      console.log(`[ScheduleV3] Preserving timeoff (ungrouped) as separate row:`, {
        timeoffCount,
        datesCount: Object.keys(timeoffGroup.dates).length
      });
      transformedGroups.push(timeoffGroup);
    }
    
    if (Object.keys(unavailabilityGroup.dates).length > 0) {
      const unavailCount = Object.values(unavailabilityGroup.dates).reduce((sum, d) => sum + (d.unavailability?.length || 0), 0);
      console.log(`[ScheduleV3] Preserving unavailability (ungrouped) as separate row:`, {
        unavailCount,
        datesCount: Object.keys(unavailabilityGroup.dates).length
      });
      transformedGroups.push(unavailabilityGroup);
    }
    
    if (Object.keys(holidayGroup.dates).length > 0) {
      const holidayCount = Object.values(holidayGroup.dates).reduce((sum, d) => sum + (d.holiday?.length || 0), 0);
      console.log(`[ScheduleV3] Preserving holiday (ungrouped) as separate row:`, {
        holidayCount,
        datesCount: Object.keys(holidayGroup.dates).length
      });
      transformedGroups.push(holidayGroup);
    }

    if (Object.keys(openShiftsGroup.dates).length > 0) {
      const openShiftsCount = Object.values(openShiftsGroup.dates).reduce((sum, d) => sum + (d.open_shifts?.length || 0), 0);
      console.log(`[ScheduleV3] Preserving open_shifts (ungrouped) as separate row:`, {
        openShiftsCount,
        datesCount: Object.keys(openShiftsGroup.dates).length
      });
      transformedGroups.push(openShiftsGroup);
    }
  });
  
  console.log('[ScheduleV3] Transformation complete:', {
    finalGroupsCount: transformedGroups.length,
    groupsWithUnavailability: transformedGroups.filter(g => 
      Object.values(g.dates).some(d => d.unavailability && d.unavailability.length > 0)
    ).length,
    groupsWithTimeoff: transformedGroups.filter(g => 
      Object.values(g.dates).some(d => d.timeoff && d.timeoff.length > 0)
    ).length,
    groupsWithHolidays: transformedGroups.filter(g => 
      Object.values(g.dates).some(d => d.holiday && d.holiday.length > 0)
    ).length,
    groupsWithOpenShifts: transformedGroups.filter(g => 
      Object.values(g.dates).some(d => d.open_shifts && d.open_shifts.length > 0)
    ).length,
    totalOpenShifts: transformedGroups.reduce((sum, g) => 
      sum + Object.values(g.dates).reduce((dateSum: number, d: any) => 
        dateSum + (d.open_shifts?.length || 0), 0
      ), 0
    ),
  });
  
  return transformedGroups;
};

const scheduleV3Slice = createSlice({
  name: 'scheduleV3',
  initialState: initialScheduleV3State,
  reducers: {
    // Filter actions
    setGroupBy: (state, action: PayloadAction<SchedulesV3GroupByType>) => {
      state.filters.groupBy = action.payload;
      state.currentGroupBy = action.payload;
    },
    setDepartmentFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.departmentCodes = action.payload;
    },
    setSiteFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.siteIds = action.payload;
    },
    setEmployeeFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.employeeIds = action.payload;
    },
    updateFilters: (state, action: PayloadAction<Partial<ScheduleV3FilterState>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    
    // UI actions
    toggleGroupExpansion: (state, action: PayloadAction<string>) => {
      const groupId = action.payload;
      const index = state.ui.expandedGroups.indexOf(groupId);
      if (index > -1) {
        state.ui.expandedGroups.splice(index, 1);
      } else {
        state.ui.expandedGroups.push(groupId);
      }
    },
    expandAllGroups: (state) => {
      state.ui.expandedGroups = state.groups.map(g => g.group_id);
    },
    collapseAllGroups: (state) => {
      state.ui.expandedGroups = [];
    },
    setSelectedDate: (state, action: PayloadAction<string | undefined>) => {
      state.ui.selectedDate = action.payload;
    },
    setSelectedGroup: (state, action: PayloadAction<string | undefined>) => {
      state.ui.selectedGroupId = action.payload;
    },
    setSelectedEmployee: (state, action: PayloadAction<string | undefined>) => {
      state.ui.selectedEmployeeId = action.payload;
    },
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.ui.viewMode = action.payload;
    },
    updateUIState: (state, action: PayloadAction<Partial<ScheduleV3UIState>>) => {
      state.ui = {
        ...state.ui,
        ...action.payload,
      };
    },
    
    // Conflict actions
    addConflict: (state, action: PayloadAction<ScheduleV3Conflict>) => {
      state.conflicts.push(action.payload);
    },
    removeConflict: (state, action: PayloadAction<string>) => {
      state.conflicts = state.conflicts.filter(c => c.scheduleId !== action.payload);
    },
    clearConflicts: (state) => {
      state.conflicts = [];
    },
    
    // Data actions
    clearScheduleV3Data: (state) => {
      state.groups = [];
      state.groupedData = {};
      state.sidebarLayout = {
        items: [],
        groupBy: null,
        isLoading: false,
        error: null,
        status: null,
        message: null,
        lastFetchedRange: null,
      };
      state.statistics = {
        totalSchedules: 0,
        totalEmployees: 0,
        totalHours: 0,
        totalGroups: 0,
      };
      state.conflicts = [];
    },
    
    // Error actions
    clearError: (state) => {
      state.error = null;
      state.lastFetchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get sidebar layout list
      .addCase(getScheduleSidebarList.pending, (state) => {
        state.sidebarLayout.isLoading = true;
        state.sidebarLayout.error = null;
      })
      .addCase(getScheduleSidebarList.fulfilled, (state, action) => {
        state.sidebarLayout.isLoading = false;
        state.sidebarLayout.items = action.payload.data || [];
        state.sidebarLayout.groupBy = action.payload.group_by;
        state.sidebarLayout.status = action.payload.status;
        state.sidebarLayout.message = action.payload.message;
        state.sidebarLayout.lastFetchedRange = action.meta.arg
          ? {
              startDate: action.meta.arg.start_date,
              endDate: action.meta.arg.end_date,
            }
          : null;
      })
      .addCase(getScheduleSidebarList.rejected, (state, action) => {
        state.sidebarLayout.isLoading = false;
        state.sidebarLayout.error = (action.payload as string) || action.error.message || 'Failed to fetch schedule sidebar data';
      })
      // Get Schedules V3
      .addCase(getSchedulesV3.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSchedulesV3.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentGroupBy = action.payload.group_by as SchedulesV3GroupByType;
        state.lastFetchTime = Date.now();
        
        // Merge new data with existing data instead of replacing
        const existingGroups = [...state.groups];
        let newGroups = action.payload.data;
        
        // Transform ungrouped data (like unavailability, timeoff) to proper employee/department groups
        newGroups = transformUngroupedData(newGroups, state.currentGroupBy, existingGroups);
        
        // Debug: Check if this is an open_shifts-only request
        const hasOpenShifts = newGroups.some(group => 
          Object.values(group.dates).some((dateData: any) => 
            Array.isArray(dateData.open_shifts) && dateData.open_shifts.length > 0
          )
        );
        if (hasOpenShifts) {
          console.log('[ScheduleV3] Merging open_shifts data:', {
            existingGroupsCount: existingGroups.length,
            newGroupsCount: newGroups.length,
            openShiftsCount: newGroups.reduce((sum, g) => 
              sum + Object.values(g.dates).reduce((dateSum: number, dateData: any) => 
                dateSum + (Array.isArray(dateData.open_shifts) ? dateData.open_shifts.length : 0), 0
              ), 0
            )
          });
        }
        
        newGroups.forEach(newGroup => {
          const existingGroupIndex = existingGroups.findIndex(g => g.group_id === newGroup.group_id);
          
          if (existingGroupIndex > -1) {
            // Group exists, merge the dates data
            const existingGroup = existingGroups[existingGroupIndex];
            const mergedDates: any = { ...existingGroup.dates };
            
            // Debug: Log what dates exist in existing vs new
            const existingDates = Object.keys(existingGroup.dates);
            const newDates = Object.keys(newGroup.dates);
            
            // Merge each date's data
            Object.keys(newGroup.dates).forEach(dateKey => {
              if (mergedDates[dateKey]) {
                // Merge data for this date
                const existingDateData = mergedDates[dateKey];
                const newDateData = newGroup.dates[dateKey];
                
                // Debug for open_shifts merge
                if (Array.isArray(newDateData.open_shifts) && newDateData.open_shifts.length > 0) {
                  console.log('[ScheduleV3] Merging date', dateKey, {
                    groupId: newGroup.group_id,
                    existingOpenShifts: existingDateData.open_shifts?.length || 0,
                    newOpenShifts: newDateData.open_shifts.length,
                    existingSchedules: existingDateData.schedules?.length || 0,
                  });
                }
                
                mergedDates[dateKey] = {
                  ...existingDateData,
                  // Only merge arrays that have data in the new response
                  // If new data has empty arrays, preserve existing arrays
                  // For arrays that have data in the new response, merge with existing and deduplicate
                  schedules: Array.isArray(newDateData.schedules) && newDateData.schedules.length > 0
                    ? [
                        ...(existingDateData.schedules || []),
                        ...newDateData.schedules
                      ].filter((schedule, index, self) => 
                        index === self.findIndex(s => s.os_emp_schedules_id === schedule.os_emp_schedules_id)
                      )
                    : (existingDateData.schedules || []),
                  timeoff: Array.isArray(newDateData.timeoff) && newDateData.timeoff.length > 0
                    ? [
                        ...(existingDateData.timeoff || []),
                        ...newDateData.timeoff
                      ].filter((item, index, self) => 
                        index === self.findIndex(t => t.schedules_id === item.schedules_id)
                      )
                    : (existingDateData.timeoff || []),
                  holiday: Array.isArray(newDateData.holiday) && newDateData.holiday.length > 0
                    ? [
                        ...(existingDateData.holiday || []),
                        ...newDateData.holiday
                      ].filter((item, index, self) => 
                        index === self.findIndex(h => h.title === item.title && h.calendar_code === item.calendar_code)
                      )
                    : (existingDateData.holiday || []),
                  unavailability: Array.isArray(newDateData.unavailability) && newDateData.unavailability.length > 0
                    ? [
                        ...(existingDateData.unavailability || []),
                        ...newDateData.unavailability
                      ].filter((item, index, self) => 
                        index === self.findIndex(u => u.unavailability_id === item.unavailability_id)
                      )
                    : (existingDateData.unavailability || []),
                  open_shifts: Array.isArray(newDateData.open_shifts) && newDateData.open_shifts.length > 0
                    ? (() => {
                        const merged = [
                          ...(existingDateData.open_shifts || []),
                          ...newDateData.open_shifts
                        ].filter((item, index, self) => 
                          index === self.findIndex(o => o.open_shift_id === item.open_shift_id)
                        );
                        if (newDateData.open_shifts.length > 0) {
                          console.log('[ScheduleV3] Merging open_shifts for date', dateKey, {
                            existing: existingDateData.open_shifts?.length || 0,
                            new: newDateData.open_shifts.length,
                            merged: merged.length
                          });
                        }
                        return merged;
                      })()
                    : (existingDateData.open_shifts || []),
                };
              } else {
                // New date, add it
                if (Array.isArray(newGroup.dates[dateKey].open_shifts) && newGroup.dates[dateKey].open_shifts.length > 0) {
                  console.log('[ScheduleV3] Adding new date with open_shifts', dateKey, {
                    groupId: newGroup.group_id,
                    openShiftsCount: newGroup.dates[dateKey].open_shifts.length,
                  });
                }
                mergedDates[dateKey] = newGroup.dates[dateKey];
              }
            });
            
            // Create a new group object to ensure Redux detects the change
            existingGroups[existingGroupIndex] = {
              ...existingGroup,
              dates: { ...mergedDates }, // Create a new dates object
            };
          } else {
            // New group, add it
            const openShiftsInGroup = Object.values(newGroup.dates).reduce((sum: number, dateData: any) => 
              sum + (Array.isArray(dateData.open_shifts) ? dateData.open_shifts.length : 0), 0
            );
            if (openShiftsInGroup > 0) {
              console.log('[ScheduleV3] Adding new group with open_shifts', {
                groupId: newGroup.group_id,
                openShiftsCount: openShiftsInGroup,
              });
            }
            existingGroups.push(newGroup);
          }
        });
        
        state.groups = existingGroups;
        
        // Debug: Log final state after merge
        if (hasOpenShifts) {
          const finalOpenShiftsCount = existingGroups.reduce((sum, g) => 
            sum + Object.values(g.dates).reduce((dateSum: number, dateData: any) => 
              dateSum + (Array.isArray(dateData.open_shifts) ? dateData.open_shifts.length : 0), 0
            ), 0
          );
          console.log('[ScheduleV3] Final state after merge:', {
            totalGroups: existingGroups.length,
            totalOpenShifts: finalOpenShiftsCount,
            groupsWithOpenShifts: existingGroups.filter(g => 
              Object.values(g.dates).some((dateData: any) => 
                Array.isArray(dateData.open_shifts) && dateData.open_shifts.length > 0
              )
            ).length,
          });
        }
        
        // Build grouped data for easier access
        const groupedData: any = {};
        existingGroups.forEach(group => {
          groupedData[group.group_id] = {
            groupName: group.group_name,
            dates: group.dates,
          };
        });
        state.groupedData = groupedData;
        
        // Calculate statistics from merged data
        let totalSchedules = 0;
        let totalHours = 0;
        const employeeSet = new Set<string>();
        
        existingGroups.forEach(group => {
          Object.values(group.dates).forEach((dateData: any) => {
            totalSchedules += dateData.schedules?.length || 0;
            
            dateData.schedules?.forEach((schedule: any) => {
              if (schedule.employee?.id) {
                employeeSet.add(schedule.employee.id);
              }
              
              // Calculate hours
              if (schedule.shift?.start_time && schedule.shift?.end_time) {
                const start = new Date(`2000-01-01 ${schedule.shift.start_time}`);
                const end = new Date(`2000-01-01 ${schedule.shift.end_time}`);
                const diffMs = end.getTime() - start.getTime();
                totalHours += diffMs / (1000 * 60 * 60);
              }
            });
          });
        });
        
        state.statistics = {
          totalSchedules,
          totalEmployees: employeeSet.size,
          totalHours,
          totalGroups: existingGroups.length,
        };
        
        // Store current date range from meta if available
        if (action.meta.arg) {
          state.currentDateRange = {
            startDate: action.meta.arg.start_date,
            endDate: action.meta.arg.end_date,
          };
        }
      })
      .addCase(getSchedulesV3.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch schedules';
        state.lastFetchError = action.error.message || 'Failed to fetch schedules';
      })
      
      // Add Schedule
      .addCase(addSchedule.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addSchedule.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optionally trigger a refresh of schedules after successful add
        // This will be handled by the component dispatching getSchedulesV3 again
      })
      .addCase(addSchedule.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to add schedule';
      });
  },
});

export const {
  setGroupBy,
  setDepartmentFilter,
  setSiteFilter,
  setEmployeeFilter,
  updateFilters,
  toggleGroupExpansion,
  expandAllGroups,
  collapseAllGroups,
  setSelectedDate,
  setSelectedGroup,
  setSelectedEmployee,
  setViewMode,
  updateUIState,
  addConflict,
  removeConflict,
  clearConflicts,
  clearScheduleV3Data,
  clearError,
} = scheduleV3Slice.actions;

export default scheduleV3Slice.reducer;
